import type { DestinationCategory, TripCard } from "./types";

/**
 * Explora's trip search, shared by the web SPA and the mobile app. A trip matches when every word
 * of the query appears somewhere in its text: name, route, summary, region, countries, cities,
 * highlights, keywords and categories. Accents and case are ignored, and the countries and best-known
 * cities also answer to their English (and other common) names, so "France", "Francia" and "Paris"
 * all find the trips through París.
 */

/** Spanish country name (as trips store it) → other names customers type. */
const COUNTRY_ALIASES: Record<string, string[]> = {
  "Alemania": ["Germany", "Deutschland"],
  "Argentina": [],
  "Austria": ["Österreich"],
  "Bélgica": ["Belgium", "Belgique"],
  "Brasil": ["Brazil"],
  "Camboya": ["Cambodia"],
  "Canadá": ["Canada"],
  "China": [],
  "Colombia": [],
  "Corea del Sur": ["South Korea", "Korea", "Corea"],
  "Egipto": ["Egypt"],
  "Emiratos Árabes Unidos": ["United Arab Emirates", "UAE", "EAU", "Emiratos"],
  "España": ["Spain"],
  "Francia": ["France"],
  "Grecia": ["Greece"],
  "Guatemala": [],
  "Hungría": ["Hungary"],
  "India": [],
  "Italia": ["Italy"],
  "Japón": ["Japan"],
  "Kenia": ["Kenya"],
  "México": ["Mexico"],
  "Namibia": [],
  "Países Bajos": ["Netherlands", "Holanda", "Holland"],
  "Perú": ["Peru"],
  "Reino Unido": ["United Kingdom", "UK", "Inglaterra", "England", "Gran Bretaña", "Great Britain"],
  "República Checa": ["Czech Republic", "Czechia", "Chequia"],
  "Sudáfrica": ["South Africa"],
  "Suiza": ["Switzerland"],
  "Tailandia": ["Thailand"],
  "Tanzania": [],
  "Turquía": ["Turkey", "Türkiye"],
  "Uganda": [],
  "Vietnam": []
};

/** City names whose English form differs beyond accents. */
const CITY_ALIASES: Record<string, string[]> = {
  "Londres": ["London"], "Bruselas": ["Brussels"], "Brujas": ["Bruges"], "Gante": ["Ghent"], "Amberes": ["Antwerp"],
  "La Haya": ["The Hague"], "Colonia": ["Cologne"], "Lucerna": ["Lucerne"], "Zúrich": ["Zurich"], "Milán": ["Milan"],
  "Venecia": ["Venice"], "Padua": ["Padova"], "Florencia": ["Florence"], "Roma": ["Rome"], "Asís": ["Assisi"],
  "Sevilla": ["Seville"], "Zaragoza": ["Saragossa"], "Burdeos": ["Bordeaux"], "Atenas": ["Athens"], "Praga": ["Prague"],
  "Viena": ["Vienna"], "Dresde": ["Dresden"], "Estambul": ["Istanbul"], "Capadocia": ["Cappadocia"], "El Cairo": ["Cairo"],
  "Asuán": ["Aswan"], "Ciudad del Cabo": ["Cape Town"], "Johannesburgo": ["Johannesburg"], "Pekín": ["Beijing", "Peking"],
  "Beijing": ["Pekín"], "Shanghái": ["Shanghai"], "Tokio": ["Tokyo"], "Kioto": ["Kyoto"], "Seúl": ["Seoul"],
  "Dubái": ["Dubai"], "Abu Dabi": ["Abu Dhabi"], "Nueva Delhi": ["New Delhi"], "Río de Janeiro": ["Rio de Janeiro"],
  "Quebec": ["Québec"], "Montreal": ["Montréal"], "Niágara": ["Niagara Falls"]
};

const CATEGORY_LABELS: Record<DestinationCategory, string> = { playa: "Playa", ciudad: "Ciudad", aventura: "Aventura", cultura: "Cultura" };

export function normalizeSearch(value: string): string {
  return value.normalize("NFD").replace(/[̀-ͯ]/g, "").toLowerCase().replace(/\s+/g, " ").trim();
}

const COUNTRY_INDEX = aliasIndex(COUNTRY_ALIASES);
const CITY_INDEX = aliasIndex(CITY_ALIASES);

function aliasIndex(table: Record<string, string[]>): Map<string, string[]> {
  return new Map(Object.entries(table).map(([name, aliases]) => [normalizeSearch(name), aliases]));
}

/** The names a country or city also answers to (for a city, only when the trip's text names it exactly). */
function aliasesOf(index: Map<string, string[]>, name: string): string[] {
  return index.get(normalizeSearch(name)) ?? [];
}

type SearchableTrip = Pick<TripCard, "name" | "routeDisplay" | "summary" | "region" | "destination" | "countries" | "cities" | "highlights"> &
  Partial<Pick<TripCard, "keywords" | "categories">>;

/** Everything a trip answers to, normalised and joined (computed once per trip per search). */
export function tripSearchText(trip: SearchableTrip): string {
  const routeStops = trip.routeDisplay.split("→").map((stop) => stop.trim());
  const places = [...trip.cities, ...routeStops];
  return normalizeSearch([
    trip.name, trip.routeDisplay, trip.summary, trip.region?.name ?? "", trip.destination?.name ?? "", trip.destination?.country ?? "",
    ...trip.countries, ...trip.countries.flatMap((country) => aliasesOf(COUNTRY_INDEX, country)),
    ...trip.cities, ...places.flatMap((place) => aliasesOf(CITY_INDEX, place)),
    ...trip.highlights, ...(trip.keywords ?? []),
    ...(trip.categories ?? []).map((category) => CATEGORY_LABELS[category] ?? category)
  ].join(" | "));
}

/** True when every word of the query appears in the trip's text (an empty query matches everything). */
export function tripMatchesQuery(trip: SearchableTrip, query: string): boolean {
  const words = normalizeSearch(query).split(" ").filter(Boolean);
  if (words.length === 0) return true;
  const text = tripSearchText(trip);
  return words.every((word) => text.includes(word));
}

/**
 * Category chips: a trip shows under the categories an administrator tagged it with, or under its
 * linked destination's category. "todos" shows everything.
 */
export function tripMatchesCategory(trip: Pick<TripCard, "destination"> & Partial<Pick<TripCard, "categories">>, category: DestinationCategory | "todos"): boolean {
  if (category === "todos") return true;
  return (trip.categories ?? []).includes(category) || trip.destination?.category === category;
}
